/**
 * Design Store
 * Manages canvas design state, layer operations, and selection
 * This is the working state of the editor, separate from static templates
 * History is managed by historyStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { nanoid } from 'nanoid';
import type { Template, TemplateVariable } from '@/types/template';
import type { Layer, GroupLayer } from '@/types/layers';
import type { BackgroundBrush } from '@/types/brush';

// Lazy import to avoid circular dependency
const recordChange = (label: string) => {
  // Dynamic import to avoid circular dependency
  import('./historyStore').then(({ useHistoryStore }) => {
    useHistoryStore.getState().recordChange(label);
  });
};

// Design store state
interface DesignState {
  // Current template
  currentTemplate: Template | null;

  // Variable values (bound content)
  variableValues: Record<string, string>;

  // Selection state
  selectedLayerIds: string[];
  hoveredLayerId: string | null;

  // Clipboard for copy/paste
  clipboard: Layer[] | null;

  // Edit mode
  isEditing: boolean;

  // Drag state
  isDragging: boolean;
  dragOffset: { x: number; y: number } | null;
}

// Design store actions
interface DesignActions {
  // Template management
  loadTemplate: (template: Template) => void;
  clearTemplate: () => void;
  updateTemplateMetadata: (updates: Partial<Template['metadata']>) => void;
  updateCanvasBackground: (background: BackgroundBrush) => void;

  // Variable management
  setVariableValue: (id: string, value: string) => void;
  setVariableValues: (values: Record<string, string>) => void;
  resetVariableValues: () => void;
  addVariable: (variable: TemplateVariable) => void;
  updateVariable: (id: string, updates: Partial<TemplateVariable>) => void;
  deleteVariable: (id: string) => void;

  // Layer CRUD
  addLayer: (layer: Layer, parentId?: string) => void;
  updateLayer: (id: string, updates: Partial<Layer>) => void;
  deleteLayer: (id: string) => void;
  duplicateLayer: (id: string) => Layer | null;

  // Layer ordering
  reorderLayers: (sourceIndex: number, destinationIndex: number, parentId?: string) => void;
  moveLayerUp: (id: string) => void;
  moveLayerDown: (id: string) => void;
  moveLayerToTop: (id: string) => void;
  moveLayerToBottom: (id: string) => void;

  // Grouping
  groupSelectedLayers: () => void;
  ungroupLayer: (id: string) => void;

  // Selection
  selectLayer: (id: string, addToSelection?: boolean) => void;
  selectLayers: (ids: string[]) => void;
  deselectLayer: (id: string) => void;
  clearSelection: () => void;
  selectAll: () => void;
  setHoveredLayer: (id: string | null) => void;

  // Visibility/Lock toggles
  toggleLayerVisibility: (id: string) => void;
  toggleLayerLock: (id: string) => void;

  // Clipboard operations
  copySelectedLayers: () => void;
  cutSelectedLayers: () => void;
  pasteLayers: () => void;

  // Edit mode
  setEditing: (editing: boolean) => void;

  // Drag state
  startDrag: (offset: { x: number; y: number }) => void;
  endDrag: () => void;

  // Utility
  getLayerById: (id: string) => Layer | undefined;
  getParentLayer: (id: string) => GroupLayer | undefined;
  flattenLayers: () => Layer[];
}

// Helper to find a layer by ID recursively
function findLayerById(layers: Layer[], id: string): Layer | undefined {
  for (const layer of layers) {
    if (layer.id === id) return layer;
    if (layer.type === 'group') {
      const found = findLayerById(layer.children, id);
      if (found) return found;
    }
  }
  return undefined;
}

// Helper to find parent of a layer
function findParentLayer(layers: Layer[], id: string, parent?: GroupLayer): GroupLayer | undefined {
  for (const layer of layers) {
    if (layer.id === id) return parent;
    if (layer.type === 'group') {
      const found = findParentLayer(layer.children, id, layer);
      if (found) return found;
    }
  }
  return undefined;
}

// Helper to update a layer recursively
function updateLayerInTree(layers: Layer[], id: string, updates: Partial<Layer>): Layer[] {
  return layers.map((layer) => {
    if (layer.id === id) {
      return { ...layer, ...updates } as Layer;
    }
    if (layer.type === 'group') {
      return {
        ...layer,
        children: updateLayerInTree(layer.children, id, updates),
      };
    }
    return layer;
  });
}

// Helper to delete a layer recursively
function deleteLayerFromTree(layers: Layer[], id: string): Layer[] {
  return layers
    .filter((layer) => layer.id !== id)
    .map((layer) => {
      if (layer.type === 'group') {
        return {
          ...layer,
          children: deleteLayerFromTree(layer.children, id),
        };
      }
      return layer;
    });
}

// Helper to flatten layers for iteration
function flattenLayerTree(layers: Layer[]): Layer[] {
  const result: Layer[] = [];
  for (const layer of layers) {
    result.push(layer);
    if (layer.type === 'group') {
      result.push(...flattenLayerTree(layer.children));
    }
  }
  return result;
}

// Deep clone a layer
function cloneLayer(layer: Layer): Layer {
  const newId = nanoid();
  const cloned = { ...layer, id: newId, name: `${layer.name} Copy` };
  if (cloned.type === 'group') {
    return {
      ...cloned,
      children: cloned.children.map(cloneLayer),
    };
  }
  return cloned;
}

// Initial state
const initialState: DesignState = {
  currentTemplate: null,
  variableValues: {},
  selectedLayerIds: [],
  hoveredLayerId: null,
  clipboard: null,
  isEditing: false,
  isDragging: false,
  dragOffset: null,
};

export const useDesignStore = create<DesignState & DesignActions>()(
  persist(
    immer((set, get) => ({
      ...initialState,

      // Template management
      loadTemplate: (template) => {
        set((state) => {
          state.currentTemplate = template;
          state.variableValues = {};
          template.variables.forEach((v) => {
            state.variableValues[v.id] = v.defaultValue;
          });
          state.selectedLayerIds = [];
        });
      },

      clearTemplate: () => {
        set((state) => {
          state.currentTemplate = null;
          state.variableValues = {};
          state.selectedLayerIds = [];
        });
      },

      updateTemplateMetadata: (updates) => {
        set((state) => {
          if (state.currentTemplate) {
            state.currentTemplate.metadata = {
              ...state.currentTemplate.metadata,
              ...updates,
              updatedAt: new Date().toISOString(),
            };
          }
        });
      },

      updateCanvasBackground: (background) => {
        set((state) => {
          if (state.currentTemplate) {
            state.currentTemplate.canvas.background = background;
          }
        });
        recordChange('Update Background');
      },

      // Variable management
      setVariableValue: (id, value) => {
        set((state) => {
          state.variableValues[id] = value;
        });
      },

      setVariableValues: (values) => {
        set((state) => {
          state.variableValues = { ...state.variableValues, ...values };
        });
      },

      resetVariableValues: () => {
        set((state) => {
          if (state.currentTemplate) {
            state.currentTemplate.variables.forEach((v) => {
              state.variableValues[v.id] = v.defaultValue;
            });
          }
        });
      },

      addVariable: (variable) => {
        set((state) => {
          if (state.currentTemplate) {
            // Check if variable ID already exists
            const exists = state.currentTemplate.variables.some(v => v.id === variable.id);
            if (!exists) {
              state.currentTemplate.variables.push(variable);
              state.variableValues[variable.id] = variable.defaultValue;
            }
          }
        });
        recordChange('Add Variable');
      },

      updateVariable: (id, updates) => {
        set((state) => {
          if (state.currentTemplate) {
            const variable = state.currentTemplate.variables.find(v => v.id === id);
            if (variable) {
              Object.assign(variable, updates);
            }
          }
        });
        recordChange('Update Variable');
      },

      deleteVariable: (id) => {
        set((state) => {
          if (state.currentTemplate) {
            state.currentTemplate.variables = state.currentTemplate.variables.filter(v => v.id !== id);
            delete state.variableValues[id];
          }
        });
        recordChange('Delete Variable');
      },

      // Layer CRUD
      addLayer: (layer, parentId) => {
        set((state) => {
          if (!state.currentTemplate) return;

          if (parentId) {
            const parent = findLayerById(state.currentTemplate.layers, parentId);
            if (parent?.type === 'group') {
              parent.children.push(layer);
            }
          } else {
            state.currentTemplate.layers.push(layer);
          }
        });
        recordChange('Add Layer');
      },

      updateLayer: (id, updates) => {
        set((state) => {
          if (!state.currentTemplate) return;
          state.currentTemplate.layers = updateLayerInTree(
            state.currentTemplate.layers,
            id,
            updates
          );
        });
        recordChange('Update Layer');
      },

      deleteLayer: (id) => {
        set((state) => {
          if (!state.currentTemplate) return;
          state.currentTemplate.layers = deleteLayerFromTree(state.currentTemplate.layers, id);
          state.selectedLayerIds = state.selectedLayerIds.filter((sid) => sid !== id);
        });
        recordChange('Delete Layer');
      },

      duplicateLayer: (id) => {
        const { currentTemplate } = get();
        if (!currentTemplate) return null;

        const layer = findLayerById(currentTemplate.layers, id);
        if (!layer) return null;

        const cloned = cloneLayer(layer);
        get().addLayer(cloned);
        return cloned;
      },

      // Layer ordering
      reorderLayers: (sourceIndex, destinationIndex, parentId) => {
        set((state) => {
          if (!state.currentTemplate) return;

          let layers: Layer[];
          if (parentId) {
            const parent = findLayerById(state.currentTemplate.layers, parentId);
            if (parent?.type !== 'group') return;
            layers = parent.children;
          } else {
            layers = state.currentTemplate.layers;
          }

          const [removed] = layers.splice(sourceIndex, 1);
          layers.splice(destinationIndex, 0, removed);
        });
        recordChange('Reorder Layers');
      },

      moveLayerUp: (id) => {
        const { currentTemplate } = get();
        if (!currentTemplate) return;

        const parent = findParentLayer(currentTemplate.layers, id);
        const layers = parent ? parent.children : currentTemplate.layers;
        const index = layers.findIndex((l) => l.id === id);

        if (index > 0) {
          get().reorderLayers(index, index - 1, parent?.id);
        }
      },

      moveLayerDown: (id) => {
        const { currentTemplate } = get();
        if (!currentTemplate) return;

        const parent = findParentLayer(currentTemplate.layers, id);
        const layers = parent ? parent.children : currentTemplate.layers;
        const index = layers.findIndex((l) => l.id === id);

        if (index < layers.length - 1) {
          get().reorderLayers(index, index + 1, parent?.id);
        }
      },

      moveLayerToTop: (id) => {
        const { currentTemplate } = get();
        if (!currentTemplate) return;

        const parent = findParentLayer(currentTemplate.layers, id);
        const layers = parent ? parent.children : currentTemplate.layers;
        const index = layers.findIndex((l) => l.id === id);

        if (index > 0) {
          get().reorderLayers(index, 0, parent?.id);
        }
      },

      moveLayerToBottom: (id) => {
        const { currentTemplate } = get();
        if (!currentTemplate) return;

        const parent = findParentLayer(currentTemplate.layers, id);
        const layers = parent ? parent.children : currentTemplate.layers;
        const index = layers.findIndex((l) => l.id === id);

        if (index < layers.length - 1) {
          get().reorderLayers(index, layers.length - 1, parent?.id);
        }
      },

      // Grouping
      groupSelectedLayers: () => {
        set((state) => {
          if (!state.currentTemplate || state.selectedLayerIds.length < 2) return;

          const layers = state.currentTemplate.layers;
          const selected = state.selectedLayerIds
            .map((id) => findLayerById(layers, id))
            .filter((l): l is Layer => l !== undefined);

          // Remove selected layers from their positions
          selected.forEach((layer) => {
            state.currentTemplate!.layers = deleteLayerFromTree(
              state.currentTemplate!.layers,
              layer.id
            );
          });

          // Create group
          const group: GroupLayer = {
            id: nanoid(),
            type: 'group',
            name: 'Group',
            position: selected[0].position,
            visible: true,
            locked: false,
            opacity: 1,
            children: selected,
          };

          state.currentTemplate.layers.push(group);
          state.selectedLayerIds = [group.id];
        });
        recordChange('Group Layers');
      },

      ungroupLayer: (id) => {
        set((state) => {
          if (!state.currentTemplate) return;

          const layer = findLayerById(state.currentTemplate.layers, id);
          if (layer?.type !== 'group') return;

          // Find index of group
          const index = state.currentTemplate.layers.findIndex((l) => l.id === id);
          if (index === -1) return;

          // Remove group and insert children in its place
          state.currentTemplate.layers.splice(index, 1, ...layer.children);
          state.selectedLayerIds = layer.children.map((c) => c.id);
        });
        recordChange('Ungroup Layers');
      },

      // Selection
      selectLayer: (id, addToSelection = false) => {
        set((state) => {
          if (addToSelection) {
            if (!state.selectedLayerIds.includes(id)) {
              state.selectedLayerIds.push(id);
            }
          } else {
            state.selectedLayerIds = [id];
          }
        });
      },

      selectLayers: (ids) => {
        set((state) => {
          state.selectedLayerIds = ids;
        });
      },

      deselectLayer: (id) => {
        set((state) => {
          state.selectedLayerIds = state.selectedLayerIds.filter((sid) => sid !== id);
        });
      },

      clearSelection: () => {
        set((state) => {
          state.selectedLayerIds = [];
        });
      },

      selectAll: () => {
        set((state) => {
          if (!state.currentTemplate) return;
          state.selectedLayerIds = state.currentTemplate.layers.map((l) => l.id);
        });
      },

      setHoveredLayer: (id) => {
        set((state) => {
          state.hoveredLayerId = id;
        });
      },

      // Visibility/Lock
      toggleLayerVisibility: (id) => {
        const { currentTemplate, updateLayer } = get();
        if (!currentTemplate) return;

        const layer = findLayerById(currentTemplate.layers, id);
        if (layer) {
          updateLayer(id, { visible: !layer.visible });
        }
      },

      toggleLayerLock: (id) => {
        const { currentTemplate, updateLayer } = get();
        if (!currentTemplate) return;

        const layer = findLayerById(currentTemplate.layers, id);
        if (layer) {
          updateLayer(id, { locked: !layer.locked });
        }
      },

      // Clipboard
      copySelectedLayers: () => {
        set((state) => {
          if (!state.currentTemplate) return;

          const selected = state.selectedLayerIds
            .map((id) => findLayerById(state.currentTemplate!.layers, id))
            .filter((l): l is Layer => l !== undefined);

          state.clipboard = selected.map(cloneLayer);
        });
      },

      cutSelectedLayers: () => {
        const { selectedLayerIds } = get();

        // Copy first
        set((state) => {
          if (!state.currentTemplate) return;

          const selected = selectedLayerIds
            .map((id) => findLayerById(state.currentTemplate!.layers, id))
            .filter((l): l is Layer => l !== undefined);

          state.clipboard = selected.map(cloneLayer);
        });

        // Then delete all at once
        set((state) => {
          if (!state.currentTemplate) return;

          selectedLayerIds.forEach((id) => {
            state.currentTemplate!.layers = deleteLayerFromTree(state.currentTemplate!.layers, id);
          });
          state.selectedLayerIds = [];
        });
        recordChange('Cut Layers');
      },

      pasteLayers: () => {
        const { clipboard } = get();
        if (!clipboard) return;

        // Add all layers without individual history entries
        set((state) => {
          if (!state.currentTemplate) return;

          const newIds: string[] = [];
          clipboard.forEach((layer) => {
            const pasted = cloneLayer(layer);
            // Offset position slightly
            if (pasted.position.mode === 'absolute') {
              pasted.position.x += 20;
              pasted.position.y += 20;
            }
            state.currentTemplate!.layers.push(pasted);
            newIds.push(pasted.id);
          });

          state.selectedLayerIds = newIds;
        });
        recordChange('Paste Layers');
      },

      // Edit mode
      setEditing: (editing) => {
        set((state) => {
          state.isEditing = editing;
        });
      },

      // Drag state
      startDrag: (offset) => {
        set((state) => {
          state.isDragging = true;
          state.dragOffset = offset;
        });
      },

      endDrag: () => {
        set((state) => {
          state.isDragging = false;
          state.dragOffset = null;
        });
      },

      // Utility
      getLayerById: (id) => {
        const { currentTemplate } = get();
        if (!currentTemplate) return undefined;
        return findLayerById(currentTemplate.layers, id);
      },

      getParentLayer: (id) => {
        const { currentTemplate } = get();
        if (!currentTemplate) return undefined;
        return findParentLayer(currentTemplate.layers, id);
      },

      flattenLayers: () => {
        const { currentTemplate } = get();
        if (!currentTemplate) return [];
        return flattenLayerTree(currentTemplate.layers);
      },
    })),
    {
      name: 'adstudio-template',
      partialize: (state) => ({
        variableValues: state.variableValues,
      }),
    }
  )
);

// Selector hooks for optimized rerenders
export const useSelectedLayers = () => useDesignStore((s) => s.selectedLayerIds);
export const useCurrentTemplate = () => useDesignStore((s) => s.currentTemplate);
export const useVariableValues = () => useDesignStore((s) => s.variableValues);
export const useIsEditing = () => useDesignStore((s) => s.isEditing);

// Backward compatibility alias (deprecated)
export const useTemplateStore = useDesignStore;
